import { isRetailProposalSourceValue } from "./proposalSources";
import { canManageProposalCommercially } from "./proposalOperationalAccess";

/** Sales Catalogue retail proposal workflow — includes finance. */
export const RETAIL_CATALOGUE_CREATE_ROLES = ["sales", "admin", "finance"] as const;
export const RETAIL_CATALOGUE_EDIT_ROLES = ["sales", "admin", "finance"] as const;
export const RETAIL_CATALOGUE_SEND_ROLES = ["sales", "admin", "finance"] as const;
export const RETAIL_CATALOGUE_ASSIGN_SALESPERSON_ROLES = ["admin", "finance"] as const;

/** Company-wide proposal list/view (not limited to own proposals). */
export const PROPOSAL_VIEW_ALL_ROLES = ["admin", "finance"] as const;

const RETAIL_CREATE_SET = new Set<string>(RETAIL_CATALOGUE_CREATE_ROLES);
const RETAIL_EDIT_SET = new Set<string>(RETAIL_CATALOGUE_EDIT_ROLES);
const RETAIL_SEND_SET = new Set<string>(RETAIL_CATALOGUE_SEND_ROLES);
const ASSIGN_SET = new Set<string>(RETAIL_CATALOGUE_ASSIGN_SALESPERSON_ROLES);
const VIEW_ALL_SET = new Set<string>(PROPOSAL_VIEW_ALL_ROLES);

export function canCreateRetailCatalogueProposal(role: string | undefined | null): boolean {
  return !!role && RETAIL_CREATE_SET.has(role);
}

export function canEditRetailCatalogueProposal(role: string | undefined | null): boolean {
  return !!role && RETAIL_EDIT_SET.has(role);
}

export function canSendRetailCatalogueProposal(role: string | undefined | null): boolean {
  return !!role && RETAIL_SEND_SET.has(role);
}

export function canAssignRetailCatalogueSalesperson(role: string | undefined | null): boolean {
  return !!role && ASSIGN_SET.has(role);
}

export function canViewAllProposals(role: string | undefined | null): boolean {
  return !!role && VIEW_ALL_SET.has(role);
}

export function isSalesOwnProposalScoped(role: string | undefined | null): boolean {
  return role === "sales";
}

/** Sales may see company pipeline records, but another salesperson's unsent draft is private. */
export function canViewProposalInStaffList(
  user: { id: number; role: string },
  proposal: { salespersonId?: number | null; effectiveStage?: string | null },
): boolean {
  if (user.role !== "sales") return true;
  if (String(proposal.effectiveStage ?? "").toLowerCase() !== "draft") return true;
  return Number(proposal.salespersonId ?? 0) === user.id;
}

export type StaffProposalAccessUser = { id: number; role: string };
export type StaffProposalAccessHeader = {
  salespersonId?: number | null;
  proposalSource?: string | null;
};

export function staffCanManageProposal(
  user: StaffProposalAccessUser,
  header: StaffProposalAccessHeader,
  customer?: { salespersonId?: number | null } | null,
): boolean {
  return canManageProposalCommercially(user, header, customer);
}

/** Finance may only act on retail catalogue proposals — not D-Tools/manual/project imports. */
export function financeMayAccessRetailCatalogueProposal(header: {
  proposalSource?: string | null;
}): boolean {
  return isRetailProposalSourceValue(header.proposalSource);
}

export function isValidProposalCommercialOwnerRole(role: string | undefined | null): boolean {
  return role === "sales" || role === "admin";
}
